import { Alert, Button, List, ListRow, Section, Text } from '@ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../lib/errors';
import { isIos, isStandalone } from './install';
import {
  PushPermissionDenied,
  localDeviceId,
  pushSupported,
  useEnablePush,
  useRemoveDevice,
  usePushState,
} from './push';

/** Device-specific part of the user agent for the list: "Safari auf iPhone". */
export function deviceName(userAgent: string | null | undefined, fallback: string): string {
  if (!userAgent) return fallback;
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /Firefox\//.test(userAgent)
      ? 'Firefox'
      : /Chrome\//.test(userAgent)
        ? 'Chrome'
        : /Safari\//.test(userAgent)
          ? 'Safari'
          : null;
  const system = /iPhone/.test(userAgent)
    ? 'iPhone'
    : /iPad/.test(userAgent)
      ? 'iPad'
      : /Android/.test(userAgent)
        ? 'Android'
        : /Mac OS X/.test(userAgent)
          ? 'Mac'
          : /Windows/.test(userAgent)
            ? 'Windows'
            : /Linux/.test(userAgent)
              ? 'Linux'
              : null;
  if (browser && system) return `${browser} · ${system}`;
  return browser ?? system ?? fallback;
}

/** Push on this device (asked only on click) and the list of all devices (NOT-007). */
export function PushDevices() {
  const { t } = useTranslation();
  const state = usePushState();
  const enable = useEnablePush();
  const remove = useRemoveDevice();
  const supported = pushSupported();
  const here = localDeviceId();

  if (!state.data) {
    return state.error ? <Alert tone="danger">{errorMessage(state.error)}</Alert> : null;
  }
  const { enabled, public_key: publicKey, devices } = state.data;

  let action: ReactNode = null;
  let note: string | null = null;
  if (!enabled || !publicKey) note = t('push.serverOff');
  else if (!supported)
    note = isIos() && !isStandalone() ? t('push.iosInstall') : t('push.unsupported');
  else if (typeof Notification !== 'undefined' && Notification.permission === 'denied')
    note = t('push.denied');
  else
    action = (
      <Button
        variant="secondary"
        loading={enable.isPending}
        onClick={() => enable.mutate(publicKey)}
      >
        {t('push.enable')}
      </Button>
    );

  const denied = enable.error instanceof PushPermissionDenied;
  return (
    <Section title={t('push.title')} variant="content">
      {note && <Text tone="muted">{note}</Text>}
      {enable.error && (
        <Alert tone="danger">{denied ? t('push.denied') : errorMessage(enable.error)}</Alert>
      )}
      {remove.error && <Alert tone="danger">{errorMessage(remove.error)}</Alert>}
      {action}
      {devices.length > 0 && (
        <List>
          {devices.map((d) => {
            const local = d.id === here;
            return (
              <ListRow
                key={d.id}
                title={
                  deviceName(d.user_agent, t('push.device')) +
                  (local ? ` (${t('push.thisDevice')})` : '')
                }
                description={t('push.since', {
                  date: new Date(d.created_at).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  }),
                })}
                trailing={
                  <Button
                    variant="plain"
                    size="sm"
                    onClick={() => remove.mutate({ id: d.id, local })}
                  >
                    {t('push.remove')}
                  </Button>
                }
              />
            );
          })}
        </List>
      )}
    </Section>
  );
}
